/*
 * 위시리스트(하트 북마크) 기능 - 무료, 브라우저 저장(localStorage) 방식
 * - <wishlist-button data-product-handle="..."> : 하트 토글 버튼
 * - <wishlist-list> : 저장한 제품 목록을 보여주는 위시리스트 페이지
 */
(function () {
  const KEY = 'theme-wishlist';

  const HEART_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-heart" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path fill-rule="evenodd" d="M10 5.24 8.515 3.773a4.433 4.433 0 0 0-6.21 0 4.293 4.293 0 0 0 0 6.128L10 17.495l7.695-7.593a4.293 4.293 0 0 0 0-6.128 4.433 4.433 0 0 0-6.21 0zm.765-2.177c2.113-2.084 5.538-2.084 7.65 0a5.29 5.29 0 0 1 0 7.55l-7.695 7.593a1.03 1.03 0 0 1-1.44 0l-7.696-7.594a5.29 5.29 0 0 1 0-7.549C3.697.98 7.122.98 9.234 3.063l.766.755z"/></svg>';

  function getList() {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function saveList(list) {
    localStorage.setItem(KEY, JSON.stringify(list));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { list: list } }));
  }

  function has(handle) {
    return getList().indexOf(handle) !== -1;
  }

  function toggle(handle) {
    const list = getList();
    const index = list.indexOf(handle);
    if (index === -1) {
      list.push(handle);
    } else {
      list.splice(index, 1);
    }
    saveList(list);
    return list.indexOf(handle) !== -1;
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function strings() {
    return window.wishlistStrings || {};
  }

  // Shopify money format, e.g. "{{amount_with_comma_separator}} kr", so wishlist prices match the rest of the store.
  function formatMoney(cents) {
    const format =
      (strings().showCurrencyCode ? strings().moneyWithCurrencyFormat : strings().moneyFormat) || '{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return format;

    function group(precision, thousands, decimal) {
      const parts = (cents / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }

    const amounts = {
      amount: () => group(2, ',', '.'),
      amount_no_decimals: () => group(0, ',', '.'),
      amount_with_comma_separator: () => group(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => group(0, '.', ','),
      amount_with_space_separator: () => group(2, ' ', ','),
      amount_no_decimals_with_space_separator: () => group(0, ' ', ','),
      amount_with_period_and_space_separator: () => group(2, ' ', '.'),
      amount_with_apostrophe_separator: () => group(2, "'", '.'),
    };
    const amount = (amounts[match[1]] || amounts.amount)();
    return format.replace(placeholder, amount);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // 다른 코드에서도 쓸 수 있게 공개
  window.Wishlist = { getList: getList, has: has, toggle: toggle };

  /* 하트 토글 버튼 */
  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.productHandle;
      this.button = this.querySelector('button');
      if (!this.handle || !this.button) return;
      this.update();
      this._onClick = this.onClick.bind(this);
      this._onUpdate = this.update.bind(this);
      this.button.addEventListener('click', this._onClick);
      document.addEventListener('wishlist:updated', this._onUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:updated', this._onUpdate);
    }

    onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      toggle(this.handle);
    }

    update() {
      const active = has(this.handle);
      this.classList.toggle('is-active', active);
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }
  customElements.define('wishlist-button', WishlistButton);

  /* 위시리스트 페이지: 저장한 제품 목록 표시 */
  class WishlistList extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this._onUpdate = this.render.bind(this);
      this.render();
      document.addEventListener('wishlist:updated', this._onUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:updated', this._onUpdate);
    }

    render() {
      const handles = getList();

      if (!handles.length) {
        if (this.grid) this.grid.innerHTML = '';
        if (this.empty) this.empty.hidden = false;
        return;
      }
      if (this.empty) this.empty.hidden = true;

      Promise.all(
        handles.map((handle) =>
          fetch(rootUrl() + 'products/' + handle + '.js')
            .then((res) => (res.ok ? res.json() : null))
            .catch(() => null)
        )
      ).then((products) => {
        if (!this.grid) return;
        this.grid.innerHTML = products
          .filter(Boolean)
          .map((product) => this.cardHtml(product))
          .join('');
      });
    }

    cardHtml(product) {
      const url = rootUrl() + 'products/' + product.handle;
      const imageSrc = product.featured_image
        ? escapeHtml(product.featured_image) + (product.featured_image.indexOf('?') === -1 ? '?' : '&') + 'width=400'
        : '';
      const image = imageSrc
        ? '<img src="' + imageSrc + '" alt="' + escapeHtml(product.title) + '" loading="lazy">'
        : '';

      return (
        '<li class="wishlist-card">' +
        '<wishlist-button class="wishlist-card__remove" data-product-handle="' +
        escapeHtml(product.handle) +
        '">' +
        '<button type="button" class="wishlist-button is-on-card" aria-pressed="true" aria-label="' +
        escapeHtml((strings().remove || '[product]').replace('[product]', () => product.title)) +
        '">' +
        '<span class="svg-wrapper">' +
        HEART_SVG +
        '</span></button></wishlist-button>' +
        '<a href="' +
        url +
        '" class="wishlist-card__link">' +
        '<div class="wishlist-card__media media media--transparent">' +
        image +
        '</div>' +
        '<div class="wishlist-card__info">' +
        '<span class="wishlist-card__title">' +
        escapeHtml(product.title) +
        '</span>' +
        '<span class="wishlist-card__price">' +
        escapeHtml(formatMoney(product.price)) +
        '</span>' +
        '</div></a></li>'
      );
    }
  }
  customElements.define('wishlist-list', WishlistList);

  /* 헤더 하트 아이콘의 저장 개수 표시 */
  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.bubble = this.querySelector('[data-wishlist-count]');
      this.number = this.bubble ? this.bubble.querySelector('span') : null;
      this._onUpdate = this.update.bind(this);
      this.update();
      document.addEventListener('wishlist:updated', this._onUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:updated', this._onUpdate);
    }

    update() {
      if (!this.bubble) return;
      const count = getList().length;
      if (this.number) this.number.textContent = count;
      this.bubble.hidden = count === 0;
    }
  }
  customElements.define('wishlist-count', WishlistCount);
})();
